import { __awaiter } from '../../_virtual/_tslib.js';
import { useQueryClient, useQuery, useMutation } from 'react-query';
import API from '../const.js';
import { getAuthIdParam } from '../../methods/getAuthIdParam.js';
import { getAuthHeader } from '../utils/getAuthHeader.js';
import { handleMutateResponse } from '../utils/handleMutateResponse.js';
import { QueryNames } from './queryNames.js';
import { validateAndConvertReqParamToInt } from '../utils/validateAndConvertReqParamToNum.js';

function useGetUserProfileDetails({ companyId }, options) {
    const authHeader = getAuthHeader();
    let preloads = (authHeader === null || authHeader === void 0 ? void 0 : authHeader.token) ? "&preload=agent_details&preload=material_types" : "";
    const queryClient = useQueryClient();
    return useQuery([QueryNames.userProfile, companyId], () => {
        const validatedCompanyId = validateAndConvertReqParamToInt("companyId", companyId);
        if (!validatedCompanyId) {
            return new Promise((res) => res(undefined));
        }
        return fetch(`${API.USER_PROFILE_DETAILS}&c_i=${validatedCompanyId}${preloads}`, {
            method: "GET",
            headers: Object.assign({ Accept: "application/json", "Content-Type": "application/json" }, authHeader)
        })
            .then((res) => {
            if (res.status === 401) {
                queryClient.invalidateQueries(QueryNames.validateToken);
                throw new Error("Some Error Occurred");
            }
            if (res.status >= 400) {
                throw new Error("Some Error Occurred");
            }
            else {
                return res.json().then((jsonData) => {
                    const allData = jsonData.data;
                    return allData;
                });
            }
        })
            .catch(() => {
            throw new Error("Some Error Occurred");
        });
    }, {
        enabled: options === null || options === void 0 ? void 0 : options.enabled,
        onSuccess: options === null || options === void 0 ? void 0 : options.onSuccess,
        onError: options === null || options === void 0 ? void 0 : options.onError,
        refetchInterval: options === null || options === void 0 ? void 0 : options.refetchInterval,
        staleTime: options === null || options === void 0 ? void 0 : options.staleTime,
        cacheTime: options === null || options === void 0 ? void 0 : options.cacheTime,
        select: options === null || options === void 0 ? void 0 : options.select,
        refetchOnWindowFocus: options === null || options === void 0 ? void 0 : options.refetchOnWindowFocus
    });
}
// To Update Company Details
function useUpdateCompanyDetails() {
    const queryClient = useQueryClient();
    const apiClient = `${API.UPDATE_COMPANY_DETAILS}${getAuthIdParam()}`;
    // Removed data and error props to remove warning, can be addded when need
    const { isLoading, isError, isSuccess, mutateAsync } = useMutation(({ companyId, languageCode, companyName, serviceType, placeId, logo, routeIds, is_wa_optin, coverPicture, companyBio, establishedIn, mandateKyc, aadhaarVerifiedStatus }) => fetch(apiClient, {
        method: "POST",
        headers: Object.assign({}, getAuthHeader()),
        body: JSON.stringify({
            id: parseInt(companyId),
            language_code: languageCode,
            name: companyName === null || companyName === void 0 ? void 0 : companyName.trim(),
            service_type: serviceType,
            place_id: placeId,
            details: companyBio,
            logo,
            route_ids: routeIds,
            is_wa_optin,
            cover_picture: coverPicture,
            established_in: establishedIn ? Number(establishedIn) : undefined,
            kyc_mandate: mandateKyc,
            is_aadhaar_verified: aadhaarVerifiedStatus
        })
    }), {
        onSuccess: (res) => {
            if (res.status === 401) {
                queryClient.invalidateQueries(QueryNames.validateToken);
            }
            if (res.status === 200) {
                // Need to find alternative way to update mutate function data locally
                queryClient.invalidateQueries(QueryNames.userProfile);
                queryClient.invalidateQueries(QueryNames.companyRouteDetails);
            }
        }
    });
    return { isLoading, isError, isSuccess, mutateAsync };
}
function handleUpdateCompanyDetails(mutateFn, updateCompanyDetailsParams) {
    return __awaiter(this, void 0, void 0, function* () {
        return handleMutateResponse(mutateFn, updateCompanyDetailsParams);
    });
}
function useGetUserFaq({ companyId }, options) {
    const newOptions = Object.assign(Object.assign({}, options), { select: (data) => data.faqs.map((question) => {
            return { question: question.title, response: question.response, editId: question.id };
        }), enabled: true, staleTime: Infinity, cacheTime: Infinity });
    return useGetUserProfileDetails({ companyId }, newOptions);
}
function useGetLorryByCategory({ params, options }) {
    const queryClient = useQueryClient();
    const { lorry_type, company_id } = params;
    const { enabled, onSuccess, onError, refetchInterval, staleTime, cacheTime, retry } = options;
    return useQuery([QueryNames.userLorries.getLorriesByCategory, company_id, lorry_type], () => {
        const validatedCompanyId = validateAndConvertReqParamToInt("company_id", company_id);
        const validatedLorryType = validateAndConvertReqParamToInt("lorry_type", lorry_type);
        if (!validatedCompanyId || !validatedLorryType) {
            return { data: [] };
        }
        return fetch(`${API.GET_LORRIES_BY_CATEGORY}${getAuthIdParam()}&c_i=${validatedCompanyId}&t=${validatedLorryType}`, {
            method: "GET",
            headers: Object.assign({ Accept: "application/json", "Content-Type": "application/json" }, getAuthHeader())
        })
            .then((res) => {
            if (res.status === 401) {
                queryClient.invalidateQueries(QueryNames.validateToken);
                throw new Error("Some Error Occurred");
            }
            if (res.status >= 400) {
                throw new Error("Some Error Occurred");
            }
            else {
                return res.json();
            }
        })
            .catch(() => {
            throw new Error("Service Unavailable");
        });
    }, {
        enabled,
        onSuccess,
        onError,
        refetchInterval,
        staleTime,
        cacheTime,
        retry
    });
}
// To Add Faq
function useAddFaq() {
    const queryClient = useQueryClient();
    const apiClient = `${API.USER_PROFILE_ADD_FAQ}${getAuthIdParam()}`;
    // Removed data and error props to remove warning, can be addded when need arises
    const { isLoading, isError, isSuccess, mutateAsync } = useMutation(({ companyId, questionId, response }) => fetch(apiClient, {
        method: "POST",
        headers: Object.assign({}, getAuthHeader()),
        body: JSON.stringify({
            company_id: companyId,
            queries: [
                {
                    id: questionId,
                    response
                }
            ]
        })
    }), {
        onSuccess: (res) => {
            if (res.status === 401) {
                queryClient.invalidateQueries(QueryNames.validateToken);
            }
        }
    });
    return { isLoading, isError, isSuccess, mutateAsync };
}
function handleAddFaq(mutateFn, addFaqBody) {
    return __awaiter(this, void 0, void 0, function* () {
        return handleMutateResponse(mutateFn, addFaqBody);
    });
}
// To Edit Faq
function useEditFaq() {
    const queryClient = useQueryClient();
    const apiClient = `${API.USER_PROFILE_EDIT_FAQ}${getAuthIdParam()}`;
    // Removed data and error props to remove warning, can be addded when need arises
    const { isLoading, isError, isSuccess, mutateAsync } = useMutation(({ companyId, questionId, response }) => fetch(apiClient, {
        method: "POST",
        headers: Object.assign({}, getAuthHeader()),
        body: JSON.stringify({
            company_id: companyId,
            id: questionId,
            response
        })
    }), {
        onSuccess: (res) => {
            if (res.status === 401) {
                queryClient.invalidateQueries(QueryNames.validateToken);
            }
        }
    });
    return { isLoading, isError, isSuccess, mutateAsync };
}
function handleEditFaq(mutateFn, addFaqBody) {
    return __awaiter(this, void 0, void 0, function* () {
        return handleMutateResponse(mutateFn, addFaqBody);
    });
}
// To Add or Update Company Address
function useAddOrEditOfficeAddress() {
    const queryClient = useQueryClient();
    const apiClient = `${API.ADD_EDIT_OFFICE_ADDRESS}${getAuthIdParam()}`;
    const { isLoading, isError, isSuccess, mutateAsync } = useMutation(({ companyId, formattedAddress, landmark, pincode, placeId, id, is_main }) => fetch(apiClient, {
        method: "POST",
        headers: Object.assign({}, getAuthHeader()),
        body: JSON.stringify({
            company_id: parseInt(companyId),
            place_id: placeId,
            formatted_address: formattedAddress,
            id,
            landmark,
            pincode,
            is_main
        })
    }), {
        onSuccess: (res) => {
            if (res.status === 401) {
                queryClient.invalidateQueries(QueryNames.validateToken);
            }
            if (res.status === 200) {
                queryClient.invalidateQueries(QueryNames.companyAddressList);
            }
        }
    });
    return { isLoading, isError, isSuccess, mutateAsync };
}
function handleAddOrEditOfficeAddress(mutateFn, addOrEditOfficeAddressParams) {
    return __awaiter(this, void 0, void 0, function* () {
        return handleMutateResponse(mutateFn, addOrEditOfficeAddressParams);
    });
}
// To Delete Company Address
function useDeleteOfficeAddress() {
    const queryClient = useQueryClient();
    const apiClient = `${API.DELETE_OFFICE_ADDRESS}${getAuthIdParam()}&ad_i=`;
    const { isLoading, isError, isSuccess, mutateAsync } = useMutation(({ addressId }) => fetch(apiClient + addressId, {
        method: "DELETE",
        headers: Object.assign({ Accept: "application/json", "Content-Type": "application/json" }, getAuthHeader())
    }), {
        onSuccess: (res) => {
            if (res.status === 401) {
                queryClient.invalidateQueries(QueryNames.validateToken);
            }
            if (res.status === 200) {
                queryClient.invalidateQueries(QueryNames.companyAddressList);
            }
        }
    });
    return { isLoading, isError, isSuccess, mutateAsync };
}
function handleDeleteOfficeAddress(mutateFn, deleteOfficeAddressParams) {
    return __awaiter(this, void 0, void 0, function* () {
        return handleMutateResponse(mutateFn, deleteOfficeAddressParams);
    });
}
// Upload profile picture
function useUploadImage() {
    const queryClient = useQueryClient();
    const apiClient = `${API.UPLOAD_IMAGE}${getAuthIdParam()}`;
    const { isLoading, isError, isSuccess, mutateAsync } = useMutation(({ formData }) => fetch(apiClient, {
        method: "POST",
        headers: Object.assign({}, getAuthHeader()),
        body: formData
    }), {
        onSuccess: (res) => {
            if (res.status === 401) {
                queryClient.invalidateQueries(QueryNames.validateToken);
            }
            if (res.status === 200) {
                queryClient.invalidateQueries(QueryNames.userProfile);
            }
        }
    });
    return { isLoading, isError, isSuccess, mutateAsync };
}
function handleUploadImage(mutateFn, UploadImageParams) {
    return __awaiter(this, void 0, void 0, function* () {
        return handleMutateResponse(mutateFn, UploadImageParams);
    });
}
function useGetCompanyReviewDetails({ companyId }, options) {
    const queryClient = useQueryClient();
    return useQuery([QueryNames.companyReviewList], () => fetch(`${API.COMPANY_REVIEW_DETAILS}${getAuthIdParam()}&c_i=${companyId}`, {
        method: "GET",
        headers: Object.assign({ Accept: "application/json", "Content-Type": "application/json" }, getAuthHeader())
    }).then((res) => {
        if (res.status === 401) {
            queryClient.invalidateQueries(QueryNames.validateToken);
            throw new Error("Service not available");
        }
        else {
            return res.json().then((jsonData) => {
                const allData = jsonData.data;
                return allData;
            });
        }
    }), {
        enabled: options === null || options === void 0 ? void 0 : options.enabled,
        onSuccess: options === null || options === void 0 ? void 0 : options.onSuccess,
        onError: options === null || options === void 0 ? void 0 : options.onError,
        refetchInterval: options === null || options === void 0 ? void 0 : options.refetchInterval,
        staleTime: options === null || options === void 0 ? void 0 : options.staleTime,
        cacheTime: options === null || options === void 0 ? void 0 : options.cacheTime,
        select: options === null || options === void 0 ? void 0 : options.select
    });
}
function useGetCompanyRouteDetails({ companyId }, options) {
    const queryClient = useQueryClient();
    return useQuery([QueryNames.companyRouteDetails], () => fetch(`${API.COMPANY_ROUTE_DETAILS}${getAuthIdParam()}&c_i=${companyId}`, {
        method: "GET",
        headers: Object.assign({ Accept: "application/json", "Content-Type": "application/json" }, getAuthHeader())
    })
        .then((res) => {
        if (res.status === 401) {
            queryClient.invalidateQueries(QueryNames.validateToken);
            throw new Error("Service Unavailable");
        }
        else if (res.status >= 400) {
            throw new Error("Service Unavailable");
        }
        else {
            return res.json().then((jsonData) => {
                const allData = jsonData.data;
                return allData;
            });
        }
    })
        .catch((err) => {
        throw new Error(err);
    }), {
        enabled: options === null || options === void 0 ? void 0 : options.enabled,
        onSuccess: options === null || options === void 0 ? void 0 : options.onSuccess,
        onError: options === null || options === void 0 ? void 0 : options.onError,
        refetchInterval: options === null || options === void 0 ? void 0 : options.refetchInterval,
        staleTime: options === null || options === void 0 ? void 0 : options.staleTime,
        cacheTime: options === null || options === void 0 ? void 0 : options.cacheTime,
        select: options === null || options === void 0 ? void 0 : options.select,
        retry: options === null || options === void 0 ? void 0 : options.retry
    });
}
// Update company route
function useUpdateCompanyRouteDetails() {
    const queryClient = useQueryClient();
    const apiClient = `${API.UPDATE_COMPANY_ROUTES}${getAuthIdParam()}`;
    const { isLoading, isError, isSuccess, mutateAsync } = useMutation(({ routeIds, companyId }) => fetch(apiClient, {
        method: "POST",
        headers: Object.assign({}, getAuthHeader()),
        body: JSON.stringify({
            route_ids: routeIds,
            company_id: companyId
        })
    }), {
        onSuccess: (res) => {
            if (res.status === 401) {
                queryClient.invalidateQueries(QueryNames.validateToken);
            }
            if (res.status === 200) {
                queryClient.invalidateQueries(QueryNames.companyRouteDetails);
            }
        }
    });
    return { isLoading, isError, isSuccess, mutateAsync };
}
function handleUpdateCompanyRouteDetails(mutateFn, UpdateCompanyRouteDetailsParams) {
    return __awaiter(this, void 0, void 0, function* () {
        return handleMutateResponse(mutateFn, UpdateCompanyRouteDetailsParams);
    });
}

export { handleAddFaq, handleAddOrEditOfficeAddress, handleDeleteOfficeAddress, handleEditFaq, handleUpdateCompanyDetails, handleUpdateCompanyRouteDetails, handleUploadImage, useAddFaq, useAddOrEditOfficeAddress, useDeleteOfficeAddress, useEditFaq, useGetCompanyReviewDetails, useGetCompanyRouteDetails, useGetLorryByCategory, useGetUserFaq, useGetUserProfileDetails, useUpdateCompanyDetails, useUpdateCompanyRouteDetails, useUploadImage };
//# sourceMappingURL=useUserProfileService.js.map
